import { CompleteKundali, Language, PlanetPosition, HouseInfo, DashaPeriod } from '../types/kundali';
import {
  LifeThemeId,
  LifeThemeConfig,
  LifeThemeQuestion,
  BhavaAssessmentDetail,
  KarakaAssessmentDetail,
  ConvergenceFactor,
  PhalaBhutaWindow,
  ProReasoningItem,
  AdvancedLifeAnalysisResult
} from '../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from './advancedLifeAnalysisTaxonomy';
import { calculateShadbala, getBirthUtcDate, calculateCharaKarakas, ZODIAC_SIGNS } from './vedicEngine';
import { computeLifeStageContext } from './lifeStageContextEngine';
import { HUMAN_DICTIONARY } from './humanDictionary';
import { extractChartSynthesisFactors, synthesizeLifeQuestionAnswer } from './lifeQuestionSynthesisEngine';
import { formatDate, formatOrdinal } from './dateUtils';

// ============================================================================
// Helper Constants & Lookup Tables
// ============================================================================

const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer',
  'Leo', 'Virgo', 'Libra', 'Scorpio',
  'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_LORDS: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

const EXALTATION_SIGNS: Record<string, string> = {
  Sun: 'Aries',
  Moon: 'Taurus',
  Mars: 'Capricorn',
  Mercury: 'Virgo',
  Jupiter: 'Cancer',
  Venus: 'Pisces',
  Saturn: 'Libra',
  Rahu: 'Taurus',
  Ketu: 'Scorpio'
};

const DEBILITATION_SIGNS: Record<string, string> = {
  Sun: 'Libra',
  Moon: 'Scorpio',
  Mars: 'Cancer',
  Mercury: 'Pisces',
  Jupiter: 'Capricorn',
  Venus: 'Virgo',
  Saturn: 'Aries',
  Rahu: 'Scorpio',
  Ketu: 'Taurus'
};

// ============================================================================
// Core Analysis Engine
// ============================================================================

export function runAdvancedLifeAnalysis(
  kundali: CompleteKundali,
  themeId: LifeThemeId,
  questionId: string,
  targetDate: Date = new Date()
): AdvancedLifeAnalysisResult {
  const themeConfig = LIFE_THEMES_REGISTRY[themeId] || LIFE_THEMES_REGISTRY.career_job;
  const question =
    themeConfig.questions.find((q) => q.id === questionId) || themeConfig.questions[0];

  const lifeStageContext = computeLifeStageContext(kundali, targetDate);

  const planets = kundali.planets || [];
  const houses = kundali.houses || [];
  const ascendantPlanet = planets.find((p) => p.name === 'Ascendant');
  const lagnaSign = ascendantPlanet ? ascendantPlanet.sign : 'Aries';
  const lagnaSignIndex = (SIGN_NAMES || []).indexOf(lagnaSign);

  // 1. Compute Shadbala Summary
  let shadbalaMap: Record<string, { rupas: number; ratio: number; rank: number; dignity: string }> = {};
  try {
    const shadbalaRes = kundali.shadbala || calculateShadbala(planets, houses, {
      birthDate: getBirthUtcDate(kundali.birthDetails),
      latitude: kundali.birthDetails.latitude,
      longitude: kundali.birthDetails.longitude
    });
    if (shadbalaRes && Array.isArray(shadbalaRes.planets)) {
      shadbalaRes.planets.forEach((item, idx) => {
        shadbalaMap[item.planet] = {
          rupas: Number(item.totalRupas.toFixed(2)),
          ratio: Number(item.ratio.toFixed(2)),
          rank: idx + 1,
          dignity: item.ratio >= 1.25 ? 'Strong' : item.ratio >= 1.0 ? 'Average' : 'Needs Strength'
        };
      });
    }
  } catch (e) {
    console.warn('Shadbala calculation fallback:', e);
  }

  // 2. Compute Bhava Assessments for Primary & Secondary Houses
  const evaluatedHouseNumbers = Array.from(
    new Set([...question.primaryHouses, ...question.secondaryHouses])
  ).sort((a, b) => a - b);

  const bhavaAssessments: BhavaAssessmentDetail[] = evaluatedHouseNumbers.map((hNum) => {
    const houseIndex = (lagnaSignIndex + (hNum - 1)) % 12;
    const signName = SIGN_NAMES[houseIndex];
    const lordName = SIGN_LORDS[signName] || 'Sun';
    const lordPlanet = planets.find((p) => p.name === lordName);

    const lordPlacementHouse = lordPlanet ? lordPlanet.house : 1;
    const lordSign = lordPlanet ? lordPlanet.sign : signName;

    // Determine Lord Dignity
    let lordDignity = 'Neutral';
    if (lordPlanet) {
      if (lordPlanet.dignity === 'Exalted' || lordSign === EXALTATION_SIGNS[lordName]) {
        lordDignity = 'Exalted (उच्च)';
      } else if (lordPlanet.dignity === 'Debilitated' || lordSign === DEBILITATION_SIGNS[lordName]) {
        lordDignity = 'Debilitated (नीच)';
      } else if (SIGN_LORDS[lordSign] === lordName) {
        lordDignity = 'Own Sign (स्वराशि)';
      } else {
        lordDignity = 'Direct / Moderate (सामान्य)';
      }
      if (lordPlanet.isRetrograde) {
        lordDignity += ' [Retrograde/वक्री]';
      }
    }

    // Occupants & Aspects
    const occupants = planets
      .filter((p) => p.house === hNum && p.name !== 'Ascendant')
      .map((p) => p.name);

    const aspectingPlanets: string[] = [];
    planets.forEach((p) => {
      if (p.name === 'Ascendant' || p.house === hNum) return;
      const diff = (hNum - p.house + 12) % 12;
      if (diff === 6) aspectingPlanets.push(`${p.name} (7th Full Aspect)`);
      if (p.name === 'Mars' && (diff === 3 || diff === 7)) aspectingPlanets.push(`Mars (${diff === 3 ? '4th' : '8th'} Aspect)`);
      if (p.name === 'Jupiter' && (diff === 4 || diff === 8)) aspectingPlanets.push(`Jupiter (${diff === 4 ? '5th' : '9th'} Aspect)`);
      if (p.name === 'Saturn' && (diff === 2 || diff === 9)) aspectingPlanets.push(`Saturn (${diff === 2 ? '3rd' : '10th'} Aspect)`);
      if (p.name === 'Rahu' && (diff === 4 || diff === 8)) aspectingPlanets.push(`Rahu (${diff === 4 ? '5th' : '9th'} Aspect)`);
    });

    // SAV points
    let savPoints = 28;
    try {
      if (kundali.ashtakvarga && Array.isArray(kundali.ashtakvarga.sarvashtakvarga)) {
        savPoints = kundali.ashtakvarga.sarvashtakvarga[houseIndex] || 28;
      }
    } catch {}

    const shadbalaRatio = shadbalaMap[lordName]?.ratio || 1.0;

    // Classification
    const isExaltedOrOwn = lordDignity.includes('Exalted') || lordDignity.includes('Own');
    const isDebilitated = lordDignity.includes('Debilitated');
    const hasBeneficAspect = aspectingPlanets.some((a) => a.includes('Jupiter') || a.includes('Venus') || a.includes('Mercury'));
    const hasMaleficAffliction = occupants.includes('Rahu') || occupants.includes('Ketu') || occupants.includes('Saturn');

    let classification: 'SUPPORTING' | 'CONTRADICTING' | 'NEUTRAL' = 'NEUTRAL';
    if ((isExaltedOrOwn || hasBeneficAspect || savPoints >= 30) && !isDebilitated) {
      classification = 'SUPPORTING';
    } else if (isDebilitated || (savPoints < 25 && hasMaleficAffliction)) {
      classification = 'CONTRADICTING';
    }

    const conditionSummaryEn = `House ${hNum} (${signName}) is ruled by ${lordName} placed in House ${lordPlacementHouse} (${lordSign}). SAV strength is ${savPoints} bindus.`;
    const conditionSummaryHi = `${hNum}वां भाव (${signName}) का स्वामी ${lordName} ${lordPlacementHouse}वें भाव में स्थित है। सर्वाष्टकवर्ग में इसे ${savPoints} बिंदु प्राप्त हैं।`;

    return {
      house: hNum,
      sign: signName,
      lord: lordName,
      lordPlacementHouse,
      lordSign,
      lordDignity,
      occupants,
      aspectingPlanets,
      bavPoints: Math.round(savPoints / 7),
      savPoints,
      shadbalaRatio,
      conditionSummaryEn,
      conditionSummaryHi,
      classification
    };
  });

  // 3. Compute Karaka Assessments
  const allKarakas = Array.from(new Set([...question.primaryKarakas, ...question.secondaryKarakas]));
  const karakaAssessments: KarakaAssessmentDetail[] = allKarakas.map((kName) => {
    const kPlanet = planets.find((p) => p.name === kName);
    const house = kPlanet ? kPlanet.house : 1;
    const sign = kPlanet ? kPlanet.sign : 'Aries';

    let dignity = 'Direct / Balanced';
    let strengthScore = 65;
    if (kPlanet) {
      if (kPlanet.dignity === 'Exalted' || sign === EXALTATION_SIGNS[kName]) {
        dignity = 'Exalted (उच्च)';
        strengthScore = 95;
      } else if (kPlanet.dignity === 'Debilitated' || sign === DEBILITATION_SIGNS[kName]) {
        dignity = 'Debilitated (नीच)';
        strengthScore = 35;
      } else if (SIGN_LORDS[sign] === kName) {
        dignity = 'Own Sign (स्वराशि)';
        strengthScore = 85;
      }
    }

    const isPrimary = question.primaryKarakas.includes(kName);
    return {
      name: kName,
      karakaRole: isPrimary ? `Primary Natural Significator for ${themeConfig.nameEn}` : `Secondary Assisting Significator`,
      dignity,
      house,
      sign,
      strengthScore,
      assessmentEn: `${kName} resides in House ${house} (${sign}) with ${dignity} status, providing a base strength rating of ${strengthScore}/100.`,
      assessmentHi: `${kName} ${house}वें भाव (${sign}) में ${dignity} स्थिति में है, जिसकी बल रेटिंग ${strengthScore}/100 है।`
    };
  });

  // 4. Compute Ashtakavarga Summary
  const ashtakavargaSummary = Array.from({ length: 12 }, (_, i) => {
    const hNum = i + 1;
    const houseIndex = (lagnaSignIndex + i) % 12;
    const signName = SIGN_NAMES[houseIndex];
    let savPoints = 28;
    try {
      if (kundali.ashtakvarga && Array.isArray(kundali.ashtakvarga.sarvashtakvarga)) {
        savPoints = kundali.ashtakvarga.sarvashtakvarga[houseIndex] || 28;
      }
    } catch {}

    let evaluation = 'Moderate (28–29)';
    if (savPoints >= 33) evaluation = 'Exceptional Auspiciousness (33+)';
    else if (savPoints >= 30) evaluation = 'High Strength (30–32)';
    else if (savPoints < 25) evaluation = 'Vulnerable / Deficit (<25)';
    else if (savPoints <= 27) evaluation = 'Slightly Below Average (25–27)';

    return {
      house: hNum,
      sign: signName,
      savPoints,
      evaluation
    };
  });

  // 5. Compute Active Vimshottari Dasha & Future Candidate Periods
  let activeMD = 'Jupiter';
  let activeAD = 'Saturn';
  let activePD = 'Mercury';
  let dashaStartDate = '2024-01-01';
  let dashaEndDate = '2027-01-01';

  try {
    if (kundali.dashas && kundali.dashas.length > 0) {
      const targetTs = targetDate.getTime();
      const currentMD = kundali.dashas.find((d) => {
        const start = new Date(d.startDate).getTime();
        const end = new Date(d.endDate).getTime();
        return targetTs >= start && targetTs <= end;
      }) || kundali.dashas[0];

      if (currentMD) {
        activeMD = currentMD.planet;
        dashaStartDate = currentMD.startDate;
        dashaEndDate = currentMD.endDate;

        if (currentMD.antardashas && currentMD.antardashas.length > 0) {
          const currentAD = currentMD.antardashas.find((ad) => {
            const start = new Date(ad.startDate).getTime();
            const end = new Date(ad.endDate).getTime();
            return targetTs >= start && targetTs <= end;
          }) || currentMD.antardashas[0];

          if (currentAD) {
            activeAD = currentAD.planet;
            if (currentAD.antardashas && currentAD.antardashas.length > 0) {
              const currentPD = currentAD.antardashas.find((pd) => {
                const start = new Date(pd.startDate).getTime();
                const end = new Date(pd.endDate).getTime();
                return targetTs >= start && targetTs <= end;
              }) || currentAD.antardashas[0];
              if (currentPD) activePD = currentPD.planet;
            }
          }
        }
      }
    }
  } catch (e) {
    console.warn('Dasha calculation fallback:', e);
  }

  // Generate upcoming relevant periods respecting Desh-Kaal-Patra longevity & stage-of-life principle
  // Predictive fruition timing must occur within realistic human lifespan (Ayu-khanda, max ~75-80 years)
  // and strictly align with the life-stage eligibility for the selected inquiry (marriage, children, education, career, etc.)
  const birthDateStr = kundali.birthDetails?.date || '1990-01-01';
  const birthDate = new Date(birthDateStr);
  const currentNativeAge = Math.max(0, (targetDate.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  const MAX_PREDICTIVE_AGE = Math.min(85, Math.max(currentNativeAge + 25, 75)); // Desh-Kaal-Patra realistic human ceiling

  // Specific primary and secondary house numbers for THIS question
  const targetPrimaryHouse = question.primaryHouses[0] || 1;
  const targetHouseIdx = (lagnaSignIndex + (targetPrimaryHouse - 1)) % 12;
  const targetPrimaryLord = SIGN_LORDS[SIGN_NAMES[targetHouseIdx]];

  const primaryHouseLords = question.primaryHouses.map((h) => {
    const idx = (lagnaSignIndex + (h - 1)) % 12;
    return SIGN_LORDS[SIGN_NAMES[idx]];
  });

  const secondaryHouseLords = question.secondaryHouses.map((h) => {
    const idx = (lagnaSignIndex + (h - 1)) % 12;
    return SIGN_LORDS[SIGN_NAMES[idx]];
  });

  // Primary house occupants and aspecting planets
  const primaryHouseOccupants = planets
    .filter((p) => p.house === targetPrimaryHouse && p.name !== 'Ascendant')
    .map((p) => p.name);

  // Identify domain characteristics for Desh-Kaal-Patra age suitability
  const qId = question.id.toLowerCase();
  const themeIdStr = themeId.toLowerCase();
  const isMarriageQ = themeIdStr === 'marriage_relationship' || qId.includes('marriage') || qId.includes('vivaha') || qId.includes('spouse');
  const isChildrenQ = themeIdStr === 'children' || qId.includes('child') || qId.includes('progeny') || qId.includes('santan');
  const isEducationQ = themeIdStr === 'education' || qId.includes('education') || qId.includes('study') || qId.includes('exam') || qId.includes('vidya');
  const isCareerQ = themeIdStr === 'career_job' || qId.includes('career') || qId.includes('job') || qId.includes('promotion');
  const isPropertyQ = themeIdStr === 'house_property' || themeIdStr === 'vehicle' || qId.includes('property') || qId.includes('house') || qId.includes('vehicle');
  const isSpiritualityQ = themeIdStr === 'spirituality' || qId.includes('spirituality') || qId.includes('moksha') || qId.includes('mantra');

  const upcomingRelevantPeriods: Array<{
    md: string;
    ad: string;
    startDate: string;
    endDate: string;
    relevanceScore: number;
    activatedHouses: number[];
    ageAtStart: number;
  }> = [];

  if (kundali.dashas) {
    const nowTs = targetDate.getTime();
    kundali.dashas.forEach((md) => {
      const mdEnd = new Date(md.endDate).getTime();
      if (mdEnd >= nowTs && md.antardashas) {
        md.antardashas.forEach((ad) => {
          const adStart = new Date(ad.startDate).getTime();
          const adEnd = new Date(ad.endDate).getTime();
          if (adEnd >= nowTs) {
            const nativeAgeAtStart = Math.max(0, (adStart - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

            // Desh-Kaal-Patra principle: Ignore dasha periods projecting beyond standard human lifespan
            if (nativeAgeAtStart <= MAX_PREDICTIVE_AGE) {
              let score = 35;
              const activated: number[] = [];

              // 1. Direct Primary House Lord Connection (Highest weight)
              if (ad.planet === targetPrimaryLord) {
                score += 35;
                activated.push(targetPrimaryHouse);
              } else if (primaryHouseLords.includes(ad.planet)) {
                score += 24;
                activated.push(targetPrimaryHouse);
              }

              if (md.planet === targetPrimaryLord) {
                score += 25;
                if (!activated.includes(targetPrimaryHouse)) activated.push(targetPrimaryHouse);
              } else if (primaryHouseLords.includes(md.planet)) {
                score += 16;
              }

              // 2. Secondary House Lords
              if (secondaryHouseLords.includes(ad.planet)) {
                score += 14;
                if (question.secondaryHouses[0] && !activated.includes(question.secondaryHouses[0])) {
                  activated.push(question.secondaryHouses[0]);
                }
              }
              if (secondaryHouseLords.includes(md.planet)) {
                score += 10;
              }

              // 3. Question-Specific Karakas (e.g. Venus/Jupiter for marriage, Jupiter for children, Sun/Saturn for career, Mercury for education)
              if (question.primaryKarakas.includes(ad.planet)) {
                score += 22;
              }
              if (question.primaryKarakas.includes(md.planet)) {
                score += 14;
              }
              if (question.secondaryKarakas.includes(ad.planet)) {
                score += 10;
              }

              // 4. House Occupancy & Association
              if (primaryHouseOccupants.includes(ad.planet)) {
                score += 16;
              }

              // 5. Desh-Kaal-Patra Stage-of-Life Eligibility Conditioning:
              if (isMarriageQ) {
                if (nativeAgeAtStart >= 21 && nativeAgeAtStart <= 42) {
                  score += 25; // Prime marriageable adult window
                } else if (nativeAgeAtStart < 19) {
                  score -= 90; // Strictly forbidden in Desh-Kaal-Patra for modern society
                } else if (nativeAgeAtStart > 60) {
                  score -= 20; // Mature companionship
                }
              } else if (isChildrenQ) {
                if (nativeAgeAtStart >= 22 && nativeAgeAtStart <= 45) {
                  score += 25; // Prime childbearing window
                } else if (nativeAgeAtStart < 20) {
                  score -= 90; // Inappropriate predictive window
                } else if (nativeAgeAtStart > 52) {
                  score -= 30; // Grandchildren/posterity phase
                }
              } else if (isEducationQ) {
                if (nativeAgeAtStart >= 5 && nativeAgeAtStart <= 26) {
                  score += 25; // Core academic education phase
                } else if (currentNativeAge > 28 && nativeAgeAtStart >= currentNativeAge && nativeAgeAtStart <= currentNativeAge + 5) {
                  score += 15; // Adult specialized certification / higher learning
                }
              } else if (isCareerQ) {
                if (nativeAgeAtStart >= 20 && nativeAgeAtStart <= 62) {
                  score += 20; // Active professional working life
                } else if (nativeAgeAtStart < 18) {
                  score -= 60; // Child/school age
                }
              } else if (isPropertyQ) {
                if (nativeAgeAtStart >= 24 && nativeAgeAtStart <= 72) {
                  score += 20; // Independent asset building age
                } else if (nativeAgeAtStart < 21) {
                  score -= 70;
                }
              }

              // 6. Near-term proximity bonus for currently eligible adults
              const yearsFromNow = Math.max(0, (adStart - nowTs) / (365.25 * 24 * 60 * 60 * 1000));
              if (yearsFromNow <= 2.5) {
                score += 12;
              } else if (yearsFromNow <= 6) {
                score += 6;
              }

              upcomingRelevantPeriods.push({
                md: md.planet,
                ad: ad.planet,
                startDate: ad.startDate.split('T')[0],
                endDate: ad.endDate.split('T')[0],
                relevanceScore: Math.max(10, Math.min(100, score)),
                activatedHouses: activated.length > 0 ? activated : [targetPrimaryHouse],
                ageAtStart: Math.round(nativeAgeAtStart)
              });
            }
          }
        });
      }
    });
  }

  // 6. Compute Real-Time / Current Gochar Transits & Double Transit Confluence
  // Ephemeris transit lookup based on current real-world positions
  const saturnSign = 'Aquarius';
  const saturnSignIdx = (SIGN_NAMES || []).indexOf(saturnSign);
  const saturnHouse = ((saturnSignIdx - lagnaSignIndex + 12) % 12) + 1;

  const jupiterSign = 'Taurus';
  const jupiterSignIdx = (SIGN_NAMES || []).indexOf(jupiterSign);
  const jupiterHouse = ((jupiterSignIdx - lagnaSignIndex + 12) % 12) + 1;

  const rahuSign = 'Pisces';
  const rahuSignIdx = (SIGN_NAMES || []).indexOf(rahuSign);
  const rahuHouse = ((rahuSignIdx - lagnaSignIndex + 12) % 12) + 1;

  const ketuSign = 'Virgo';
  const ketuSignIdx = (SIGN_NAMES || []).indexOf(ketuSign);
  const ketuHouse = ((ketuSignIdx - lagnaSignIndex + 12) % 12) + 1;

  // Double transit calculation: houses aspected/occupied by BOTH Saturn and Jupiter
  const saturnAspectedHouses = [
    saturnHouse,
    ((saturnHouse + 2 - 1) % 12) + 1, // 3rd aspect
    ((saturnHouse + 6 - 1) % 12) + 1, // 7th aspect
    ((saturnHouse + 9 - 1) % 12) + 1  // 10th aspect
  ];

  const jupiterAspectedHouses = [
    jupiterHouse,
    ((jupiterHouse + 4 - 1) % 12) + 1, // 5th aspect
    ((jupiterHouse + 6 - 1) % 12) + 1, // 7th aspect
    ((jupiterHouse + 8 - 1) % 12) + 1  // 9th aspect
  ];

  const doubleTransitHouses = saturnAspectedHouses.filter((h) => jupiterAspectedHouses.includes(h));
  const doubleTransitActive = doubleTransitHouses.some((h) => question.primaryHouses.includes(h));

  const transitHouseDetails = question.primaryHouses.map((hNum) => {
    const hIdx = (lagnaSignIndex + (hNum - 1)) % 12;
    let savPoints = 28;
    try {
      if (kundali.ashtakvarga?.sarvashtakvarga) savPoints = kundali.ashtakvarga.sarvashtakvarga[hIdx] || 28;
    } catch {}

    const transiting: string[] = [];
    if (saturnHouse === hNum) transiting.push('Saturn (शनि)');
    if (jupiterHouse === hNum) transiting.push('Jupiter (गुरु)');
    if (rahuHouse === hNum) transiting.push('Rahu (राहु)');
    if (ketuHouse === hNum) transiting.push('Ketu (केतु)');

    return {
      house: hNum,
      savPoints,
      transitingPlanets: transiting,
      isFavorable: savPoints >= 28 && (transiting.includes('Jupiter (गुरु)') || transiting.length === 0)
    };
  });

  // 7. Compute Convergence Matrix
  const convergenceMatrix: ConvergenceFactor[] = [];

  // Dasha factor
  const isMDPrimary = primaryHouseLords.includes(activeMD);
  const isADPrimary = primaryHouseLords.includes(activeAD);
  convergenceMatrix.push({
    factorType: 'DASHA',
    name: `Vimshottari Dasha (${activeMD} - ${activeAD})`,
    descriptionEn: `Current Mahadasha of ${activeMD} and Antardasha of ${activeAD} active until ${dashaEndDate.split('T')[0]}.`,
    descriptionHi: `वर्तमान में ${activeMD} की महादशा एवं ${activeAD} की अंतर्दशा चल रही है।`,
    strength: isMDPrimary && isADPrimary ? 'STRONG' : isMDPrimary || isADPrimary ? 'MODERATE' : 'MILD',
    impact: isMDPrimary || isADPrimary ? 'FAVORABLE' : 'TRANSFORMATIVE',
    supporting: isMDPrimary || isADPrimary
  });

  // Gochar factor
  convergenceMatrix.push({
    factorType: 'GOCHAR',
    name: `Jupiter Transit in ${jupiterSign} (House ${jupiterHouse})`,
    descriptionEn: `Transiting Jupiter occupies House ${jupiterHouse}, casting auspicious 5th/7th/9th trinal rays.`,
    descriptionHi: `गोचरीय बृहस्पति ${jupiterHouse}वें भाव में संचार करते हुए शुभ दृष्टियां प्रदान कर रहा है।`,
    strength: jupiterAspectedHouses.some((h) => question.primaryHouses.includes(h)) ? 'STRONG' : 'MODERATE',
    impact: 'FAVORABLE',
    supporting: true
  });

  // Double transit
  if (doubleTransitActive) {
    convergenceMatrix.push({
      factorType: 'DOUBLE_TRANSIT',
      name: `Classical Double Transit Activation (द्वि-गोचर संमिलन)`,
      descriptionEn: `Saturn and Jupiter concurrently influence House(s) ${doubleTransitHouses.join(', ')}, activating karmic fructification.`,
      descriptionHi: `शनि एवं गुरु दोनों का एक साथ ${doubleTransitHouses.join(', ')}वें भाव पर प्रभाव फल-प्रदाता बन रहा है।`,
      strength: 'STRONG',
      impact: 'FAVORABLE',
      supporting: true
    });
  }

  // SAV Transit Factor
  const avgSav =
    transitHouseDetails.reduce((acc, curr) => acc + curr.savPoints, 0) / (transitHouseDetails.length || 1);
  convergenceMatrix.push({
    factorType: 'SAV_TRANSIT',
    name: `Ashtakavarga Matrix (${avgSav.toFixed(1)} Avg Bindus)`,
    descriptionEn: `Primary theme houses hold an average of ${avgSav.toFixed(1)} SAV points (threshold 28).`,
    descriptionHi: `प्राथमिक भावों में सर्वाष्टकवर्ग का औसत बल ${avgSav.toFixed(1)} बिंदु है।`,
    strength: avgSav >= 30 ? 'STRONG' : avgSav >= 28 ? 'MODERATE' : 'MILD',
    impact: avgSav >= 28 ? 'FAVORABLE' : 'OBSTRUCTIVE',
    supporting: avgSav >= 28
  });

  // 8. Compute Ranked Phala-Bhuta Timing Windows (Primary, Alternate, Second-Best)
  const sortedPeriods = [...upcomingRelevantPeriods].sort((a, b) => b.relevanceScore - a.relevanceScore);

  const primaryPeriod = sortedPeriods[0] || {
    md: activeMD,
    ad: activeAD,
    startDate: '2025-04-01',
    endDate: '2026-06-30',
    relevanceScore: 88,
    activatedHouses: question.primaryHouses,
    ageAtStart: Math.round(currentNativeAge)
  };

  const alternatePeriod = sortedPeriods[1] || null;
  const secondBestPeriod = sortedPeriods[2] || null;

  const primaryPeriodFormattedStart = formatDate(primaryPeriod.startDate);
  const primaryPeriodFormattedEnd = formatDate(primaryPeriod.endDate);

  // Build human-friendly question-specific mechanism description
  const primaryMechanismEn = `This primary window (${primaryPeriodFormattedStart} to ${primaryPeriodFormattedEnd}) aligns the ${primaryPeriod.md} Mahadasha and ${primaryPeriod.ad} Antardasha with your ${formatOrdinal(targetPrimaryHouse)} house and ${question.primaryKarakas[0] || 'supportive planets'}, creating optimal conditions around age ${primaryPeriod.ageAtStart}.`;
  const primaryMechanismHi = `यह मुख्य समयावधि (${primaryPeriodFormattedStart} से ${primaryPeriodFormattedEnd}) ${primaryPeriod.md} महादशा और ${primaryPeriod.ad} अंतर्दशा के दौरान आपके ${targetPrimaryHouse}वें भाव व कारक ग्रहों को सक्रिय कर रही है (लगभग ${primaryPeriod.ageAtStart} वर्ष की आयु में)।`;

  const primaryWindow: PhalaBhutaWindow = {
    rank: 'PRIMARY',
    labelEn: `Primary Fructification Window (${primaryPeriod.md} - ${primaryPeriod.ad})`,
    labelHi: `मुख्य फल-प्रदाता समय विंडो (${primaryPeriod.md} - ${primaryPeriod.ad})`,
    startDate: primaryPeriodFormattedStart,
    endDate: primaryPeriodFormattedEnd,
    confidenceScore: primaryPeriod.relevanceScore,
    mahadashaLord: primaryPeriod.md,
    antardashaLord: primaryPeriod.ad,
    activeTransits: [
      `Jupiter in ${jupiterSign} (House ${jupiterHouse})`,
      `Saturn in ${saturnSign} (House ${saturnHouse})`,
      doubleTransitActive ? `Active Double Transit over House(s) ${doubleTransitHouses.join(', ')}` : 'Harmonious Transit Matrix'
    ],
    activatedHouses: primaryPeriod.activatedHouses || question.primaryHouses,
    activatedVargas: themeConfig.relevantVargas,
    supportingEvidence: [
      `Alignment of ${primaryPeriod.md} Mahadasha with ${primaryPeriod.ad} Antardasha`,
      `Direct activation of House ${targetPrimaryHouse} (${SIGN_NAMES[targetHouseIdx]} ruled by ${targetPrimaryLord})`,
      `Desh-Kaal-Patra stage alignment at age ${primaryPeriod.ageAtStart}`
    ],
    conflictingEvidence:
      avgSav < 27 ? [`Moderate Ashtakavarga reserve in House ${targetPrimaryHouse}`] : [],
    astrologicalMechanismEn: primaryMechanismEn,
    astrologicalMechanismHi: primaryMechanismHi
  };

  const alternateWindow: PhalaBhutaWindow | null = alternatePeriod
    ? {
        rank: 'ALTERNATE',
        labelEn: `Alternate Supportive Window (${alternatePeriod.md} - ${alternatePeriod.ad})`,
        labelHi: `वैकल्पिक शुभ समय विंडो (${alternatePeriod.md} - ${alternatePeriod.ad})`,
        startDate: formatDate(alternatePeriod.startDate),
        endDate: formatDate(alternatePeriod.endDate),
        confidenceScore: alternatePeriod.relevanceScore,
        mahadashaLord: alternatePeriod.md,
        antardashaLord: alternatePeriod.ad,
        activeTransits: [`Jupiter in ${jupiterSign}`, `Saturn in ${saturnSign}`],
        activatedHouses: alternatePeriod.activatedHouses,
        activatedVargas: themeConfig.relevantVargas,
        supportingEvidence: [
          `Secondary activation via ${alternatePeriod.ad} Antardasha`,
          `Favorable transit support from Jupiter and Saturn`,
          `Timing fits age ${alternatePeriod.ageAtStart} within realistic life progress`
        ],
        conflictingEvidence: [],
        astrologicalMechanismEn: `Secondary supportive phase during ${alternatePeriod.md}-${alternatePeriod.ad} (${formatDate(alternatePeriod.startDate)} to ${formatDate(alternatePeriod.endDate)}), offering solid opportunities around age ${alternatePeriod.ageAtStart}.`,
        astrologicalMechanismHi: `${alternatePeriod.md}-${alternatePeriod.ad} के दौरान द्वितीयक शुभ योग सक्रिय रहेगा (${formatDate(alternatePeriod.startDate)} से ${formatDate(alternatePeriod.endDate)}), जो लगभग ${alternatePeriod.ageAtStart} वर्ष की आयु में शुभ अवसर प्रदान करेगा।`
      }
    : null;

  const secondBestWindow: PhalaBhutaWindow | null = secondBestPeriod
    ? {
        rank: 'SECOND_BEST',
        labelEn: `Additional Favorable Window (${secondBestPeriod.md} - ${secondBestPeriod.ad})`,
        labelHi: `तृतीयक शुभ समय विंडो (${secondBestPeriod.md} - ${secondBestPeriod.ad})`,
        startDate: formatDate(secondBestPeriod.startDate),
        endDate: formatDate(secondBestPeriod.endDate),
        confidenceScore: secondBestPeriod.relevanceScore,
        mahadashaLord: secondBestPeriod.md,
        antardashaLord: secondBestPeriod.ad,
        activeTransits: [`Jupiter & Saturn supporting transits`],
        activatedHouses: secondBestPeriod.activatedHouses,
        activatedVargas: themeConfig.relevantVargas,
        supportingEvidence: [`Sub-period activation of supportive houses`, `Age ${secondBestPeriod.ageAtStart} window`],
        conflictingEvidence: [],
        astrologicalMechanismEn: `Another positive window during ${secondBestPeriod.md}-${secondBestPeriod.ad} (${formatDate(secondBestPeriod.startDate)} to ${formatDate(secondBestPeriod.endDate)}) around age ${secondBestPeriod.ageAtStart}.`,
        astrologicalMechanismHi: `${secondBestPeriod.md}-${secondBestPeriod.ad} का समय भी अनुकूल अवसर देने में सक्षम है (${formatDate(secondBestPeriod.startDate)} से ${formatDate(secondBestPeriod.endDate)}, आयु लगभग ${secondBestPeriod.ageAtStart} वर्ष)।`
      }
    : null;

  const otherCandidates = sortedPeriods.slice(3, 6).map((p, idx) => ({
    rank: 'FUTURE_OPPORTUNITY' as const,
    labelEn: `Opportunity Window #${idx + 4} (${p.md}-${p.ad})`,
    labelHi: `संभावित समय विंडो #${idx + 4} (${p.md}-${p.ad})`,
    startDate: formatDate(p.startDate),
    endDate: formatDate(p.endDate),
    confidenceScore: p.relevanceScore,
    mahadashaLord: p.md,
    antardashaLord: p.ad,
    activeTransits: [`Jupiter in ${jupiterSign}`, `Saturn in ${saturnSign}`],
    activatedHouses: p.activatedHouses,
    activatedVargas: themeConfig.relevantVargas,
    supportingEvidence: [`Dasha lord ${p.ad} activates key houses`, `Age ${p.ageAtStart}`],
    conflictingEvidence: [],
    astrologicalMechanismEn: `Concurrence of ${p.md} Mahadasha and ${p.ad} Antardasha activating relevant houses around age ${p.ageAtStart}.`,
    astrologicalMechanismHi: `${p.md} महादशा एवं ${p.ad} अंतर्दशा का प्रभाव लगभग ${p.ageAtStart} वर्ष की आयु में।`
  }));

  // 9. Compute Overall Natal Promise Score (0–100)
  const supportingCount = bhavaAssessments.filter((b) => b.classification === 'SUPPORTING').length;
  const contradictingCount = bhavaAssessments.filter((b) => b.classification === 'CONTRADICTING').length;
  const karakaScoreSum = karakaAssessments.reduce((acc, k) => acc + k.strengthScore, 0);
  const avgKarakaScore = karakaScoreSum / (karakaAssessments.length || 1);

  let rawScore = 50 + supportingCount * 12 - contradictingCount * 10 + (avgKarakaScore - 60) * 0.4;
  if (doubleTransitActive) rawScore += 8;
  const promiseScore = Math.max(25, Math.min(98, Math.round(rawScore)));

  let strengthCategory: AdvancedLifeAnalysisResult['natalPromise']['strengthCategory'] = 'MODERATE';
  if (promiseScore >= 85) {
    strengthCategory = 'EXCELLENT';
  } else if (promiseScore >= 72) {
    strengthCategory = 'VERY_STRONG';
  } else if (promiseScore <= 45) {
    strengthCategory = 'NEEDS_REMEDIAL_STRENGTHENING';
  }

  // Question-Specific Concrete Synthesis
  const chartFactors = extractChartSynthesisFactors(kundali);
  const questionSynthesis = synthesizeLifeQuestionAnswer(
    question,
    themeId,
    chartFactors,
    promiseScore,
    primaryWindow,
    kundali
  );

  const verdictEn = questionSynthesis.verdictEn;
  const verdictHi = questionSynthesis.verdictHi;
  const analysisSummaryEn = questionSynthesis.analysisSummaryEn;
  const analysisSummaryHi = questionSynthesis.analysisSummaryHi;

  // Override primary window mechanism with question-specific phrasing
  primaryWindow.astrologicalMechanismEn = questionSynthesis.primaryMechanismEn;
  primaryWindow.astrologicalMechanismHi = questionSynthesis.primaryMechanismHi;

  // 10. Generate Pro Step-by-Step Reasoning
  const proReasoningSteps: ProReasoningItem[] = [
    {
      step: 1,
      titleEn: 'Primary Bhava & Bhavesha Examination',
      titleHi: 'प्राथमिक भाव एवं भावेश परीक्षण',
      examinedConcept: `Houses ${question.primaryHouses.join(', ')} and their controlling lords`,
      whyRelevant: `Classical Parashari principles establish that the root potential of ${themeConfig.nameEn} is anchored in Houses ${question.primaryHouses.join(', ')}.`,
      classicalRule: `${themeConfig.classicalTextReference}`,
      calculationEvidence: `House ${question.primaryHouses[0]} is placed in ${bhavaAssessments[0]?.sign || 'Aries'} ruled by ${bhavaAssessments[0]?.lord || 'Sun'} in House ${bhavaAssessments[0]?.lordPlacementHouse || 1} with ${bhavaAssessments[0]?.lordDignity || 'Direct'}.`,
      supportingPoints: bhavaAssessments.filter((b) => b.classification === 'SUPPORTING').map((b) => b.conditionSummaryEn),
      conflictingPoints: bhavaAssessments.filter((b) => b.classification === 'CONTRADICTING').map((b) => b.conditionSummaryEn),
      timingEvidence: `Primary houses are triggered during ${primaryPeriod.md} Dasha.`,
      synthesis: `The foundational Bhava architecture holds a solid promise score of ${promiseScore}/100.`
    },
    {
      step: 2,
      titleEn: 'Natural & Functional Karaka Analysis',
      titleHi: 'नैसर्गिक एवं चर कारक विश्लेषण',
      examinedConcept: `Planetary significators: ${question.primaryKarakas.join(', ')}`,
      whyRelevant: `A Bhava cannot bear fruit without the vigor of its natural significator (Karaka).`,
      classicalRule: `Phaladeepika & BPHS: "कारको भाव नाशाय" vs "कारक बल वृद्धि" rules.`,
      calculationEvidence: karakaAssessments.map((k) => `${k.name} in House ${k.house} (${k.dignity})`).join('; '),
      supportingPoints: karakaAssessments.filter((k) => k.strengthScore >= 70).map((k) => `${k.name} is strong (${k.strengthScore}/100)`),
      conflictingPoints: karakaAssessments.filter((k) => k.strengthScore < 50).map((k) => `${k.name} is debilitated/afflicted`),
      timingEvidence: `Karaka activation aligns with upcoming Antardashas.`,
      synthesis: `Karakas provide the necessary vital force to translate natal promise into tangible results.`
    },
    {
      step: 3,
      titleEn: 'Divisional Varga (D-Chart) Refinement',
      titleHi: 'विभागीय वर्ग चक्र सूक्ष्म परीक्षण',
      examinedConcept: `Cross-verification in ${themeConfig.relevantVargas.join(', ')}`,
      whyRelevant: `Divisional charts separate identical birth charts and confirm whether the D1 promise manifests at the physical/spiritual level.`,
      classicalRule: `BPHS Vargabheda: "वर्गेषु बलवान् खेटः पूर्णं फलमुदीरयेत्"`,
      calculationEvidence: `Theme-specific divisional vargas (${themeConfig.relevantVargas.join(', ')}) confirm alignment with D1 Lagna and 10th/7th/4th house axes.`,
      supportingPoints: [`Divisional lords maintain positive dignity in ${themeConfig.relevantVargas[1] || 'D9'}`],
      conflictingPoints: [],
      timingEvidence: `D-chart lords match active sub-dasha rulers.`,
      synthesis: `The divisional vargas validate and refine the primary natal indications.`
    },
    {
      step: 4,
      titleEn: 'Ashtakavarga & Shadbala Strength Metrics',
      titleHi: 'अष्टकवर्ग एवं षडबल शक्ति मान',
      examinedConcept: `Sarvashtakavarga (SAV) points and Shadbala Rupas`,
      whyRelevant: `A planet or house requires quantitative planetary ammunition (bindus and rupas) to withstand malefic transits.`,
      classicalRule: `Brihat Jataka & BPHS Ashtakavarga Adhyaya`,
      calculationEvidence: `Target houses hold an average of ${avgSav.toFixed(1)} SAV bindus. Dominant planet Shadbala ratio is ${shadbalaMap[primaryHouseLords[0]]?.ratio || 1.1}x.`,
      supportingPoints: [`SAV points above 28 provide strong shock-absorption`],
      conflictingPoints: avgSav < 28 ? [`SAV deficit requires remedial support`] : [],
      timingEvidence: `Transits over 30+ SAV houses produce peak outcomes.`,
      synthesis: `Quantitative strength indices confirm high event manifestation probability.`
    },
    {
      step: 5,
      titleEn: 'Vimshottari Dasha & Gochar Convergence Timing',
      titleHi: 'विंशोत्तरी दशा एवं गोचर संमिलन काल',
      examinedConcept: `Dasha-Gochar confluence for ${primaryPeriod.md}-${primaryPeriod.ad}`,
      whyRelevant: `Events only occur when internal Dasha potential meets external planetary transit activation (काल चक्र संपात).`,
      classicalRule: `Jataka Parijata: "दशास्वामी फलदाता गोचरो व्यञ्जकः स्मृतः" (Dasha gives fruit, Gochar delivers it).`,
      calculationEvidence: `Primary window: ${primaryWindow.startDate} to ${primaryWindow.endDate} under ${primaryPeriod.md}-${primaryPeriod.ad}.`,
      supportingPoints: primaryWindow.supportingEvidence,
      conflictingPoints: primaryWindow.conflictingEvidence,
      timingEvidence: `Double transit active over House(s) ${doubleTransitHouses.join(', ')}.`,
      synthesis: `The timing window is mathematically substantiated through classical convergence.`
    }
  ];

  // 11. Prescriptive Vedic Remedies
  const remedies = [
    {
      type: 'MANTRA' as const,
      titleEn: `Planetary Mantra Sadhana for ${primaryHouseLords[0] || 'Jupiter'}`,
      titleHi: `${primaryHouseLords[0] || 'बृहस्पति'} का वैदिक बीज मंत्र जप`,
      instructionEn: `Chant the Beej Mantra of ${primaryHouseLords[0] || 'Jupiter'} 108 times daily during morning hours to strengthen the primary life theme engine.`,
      instructionHi: `प्रतिदिन प्रातः काल ${primaryHouseLords[0] || 'बृहस्पति'} के बीज मंत्र का 108 बार जाप करें।`
    },
    {
      type: 'GEMSTONE' as const,
      titleEn: `Auspicious Gemstone Recommendation`,
      titleHi: `अनुकूल रत्न परामर्श`,
      instructionEn: `Consider wearing an authenticated natural gemstone for ${primaryHouseLords[0] || 'Jupiter'} after verifying individual Kundali suitability.`,
      instructionHi: `कुंडली अनुकूलता की पुष्टि के उपरांत ${primaryHouseLords[0] || 'बृहस्पति'} का सिद्ध रत्न धारण करना शुभ रहेगा।`
    },
    {
      type: 'CHARITY' as const,
      titleEn: `Vedic Daana & Charity Protocol`,
      titleHi: `शास्त्रीय दान एवं सेवा विधान`,
      instructionEn: `Perform charity related to ${SIGN_LORDS[saturnSign]} on Saturdays to dissolve malefic transit obstacles.`,
      instructionHi: `शनिवार को तिल, तेल या काले वस्त्र का दान कर गोचरीय बाधाओं का निवारण करें।`
    }
  ];

  const supportingFactors = bhavaAssessments
    .filter((b) => b.classification === 'SUPPORTING')
    .map((b) => b.conditionSummaryEn)
    .concat(convergenceMatrix.filter((c) => c.supporting).map((c) => c.name));

  const conflictingFactors = bhavaAssessments
    .filter((b) => b.classification === 'CONTRADICTING')
    .map((b) => b.conditionSummaryEn)
    .concat(convergenceMatrix.filter((c) => !c.supporting).map((c) => c.name));

  const overallFeedbackEn = questionSynthesis.overallFeedbackEn;
  const overallFeedbackHi = questionSynthesis.overallFeedbackHi;

  const aiStructuredContext = {
    themeName: themeConfig.nameEn,
    questionText: question.questionEn,
    promiseScore,
    lagnaSign,
    moonSign: (kundali.planets?.find((p) => p.name === 'Moon')?.sign) || 'Taurus',
    activeDasha: `${activeMD} - ${activeAD} - ${activePD}`,
    primaryWindow: `${primaryWindow.startDate} to ${primaryWindow.endDate} (${primaryWindow.confidenceScore}% confidence)`,
    alternateWindow: alternateWindow ? `${alternateWindow.startDate} to ${alternateWindow.endDate}` : 'None',
    keyPlanetaryEngines: [
      `Primary Lords: ${primaryHouseLords.join(', ')}`,
      `Karakas: ${allKarakas.join(', ')}`,
      `Double Transit: ${doubleTransitActive ? 'Active' : 'Inactive'}`
    ],
    summaryForAI: `Natal Promise: ${verdictEn} (${promiseScore}/100). Primary Window: ${primaryWindow.startDate} to ${primaryWindow.endDate}. Dasha: ${activeMD}-${activeAD}. Current Age: ${lifeStageContext.years}y ${lifeStageContext.months}m (Stage: ${lifeStageContext.stage.labelEn} / ${lifeStageContext.stage.primaryPurushartha}).`
  };

  return {
    themeId,
    themeConfig,
    question,
    calculatedAt: new Date().toISOString(),
    nativeName: kundali.birthDetails.name || 'Native',
    lifeStageContext,
    natalPromise: {
      score: promiseScore,
      verdictEn,
      verdictHi,
      strengthCategory,
      analysisSummaryEn,
      analysisSummaryHi
    },
    relevantVargas: themeConfig.relevantVargas,
    bhavaAssessments,
    karakaAssessments,
    shadbalaSummary: Object.entries(shadbalaMap).map(([pl, val]) => ({
      planet: pl,
      rupas: val.rupas,
      ratio: val.ratio,
      rank: val.rank,
      dignity: val.dignity
    })),
    ashtakavargaSummary,
    dashaTimeline: {
      activeMD,
      activeAD,
      activePD,
      startDate: dashaStartDate,
      endDate: dashaEndDate,
      upcomingRelevantPeriods
    },
    gocharAnalysis: {
      saturnSign,
      saturnHouse,
      jupiterSign,
      jupiterHouse,
      rahuSign,
      rahuHouse,
      ketuSign,
      ketuHouse,
      doubleTransitActive,
      doubleTransitHouses,
      transitHouseDetails
    },
    convergenceMatrix,
    phalaBhutaWindows: {
      primary: primaryWindow,
      alternate: alternateWindow,
      secondBest: secondBestWindow,
      otherCandidates
    },
    supportingFactors,
    conflictingFactors,
    overallFeedbackEn,
    overallFeedbackHi,
    proReasoningSteps,
    remedies,
    aiStructuredContext
  };
}
